import React, {useState} from 'react';
import Logo from '../../assets/icons/CampusLLMLogoSmall.png';
import NewChatIconWhite from "../../assets/icons/EditIconWhite.svg";
import MenuIconWhite from "../../assets/icons/burger.svg";
import CourseIconWhite from "../../assets/icons/graduation-cap.svg";
import EventIconWhite from "../../assets/icons/Calendar_Days.svg";
import NetworkingIcon from "../../assets/icons/Notebook.svg";

import {
    LuMenu,
    LuX,
    LuArrowLeft,
    LuHome,
    LuSearch,
    LuBookOpen,
    LuCalendar,
    LuRefreshCw,
    LuMessagesSquare, LuMenuSquare, LuPanelLeftOpen, LuPanelLeftClose
} from "react-icons/lu";
import {useAuth} from "../../context/AuthContext";
import {useNavigate} from "react-router-dom";

// SVG icon wrapper component that handles dark/light mode automatically
const AdaptiveIcon = ({ src, className = "" }) => {
    return (
        <div className={`${className} flex items-center justify-center`}>
            <img
                src={src}
                className="w-full h-full dark:invert"
                alt="Icon"
            />
        </div>
    );
};

export default function LeftNav({ isPhone, isExpanded, onNewConversation, toggleNavExpanded }) {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [currentLevel, setCurrentLevel] = useState('main'); // 'main' or specific section like 'courses'
    const [slideDirection, setSlideDirection] = useState('none'); // 'left', 'right', 'none'
    const [isAnimating, setIsAnimating] = useState(false);

    // Handler for new conversation
    const handleNewConversation = () => {
        navigate("/"); // Navigate to home route first
        if (onNewConversation) {
            setTimeout(() => {
                onNewConversation();
            }, 100); // Slight delay to ensure navigation happens first
        }
    };

    // Configuration for sections with submenus
    const sectionsConfig = {
        courses: {
            hasSubmenu: true,
            navItems: [
                {
                    icon: LuSearch,
                    label: "Browse",
                    onClick: () => navigate("/courses"),
                },
                {
                    icon: LuBookOpen,
                    label: "Schedules",
                    onClick: () => navigate("/courses/schedule"),
                    comingSoon: true,
                }
            ]
        }
        // Add more sections with submenus here as needed
    };

    // Handle navigation to secondary level
    const handleSecondaryNav = (section) => {
        const sectionConfig = sectionsConfig[section];
        const hasSubmenu = sectionConfig?.hasSubmenu || false;

        if (hasSubmenu) {
            setIsAnimating(true);
            setSlideDirection('left');
            
            setTimeout(() => {
                setCurrentLevel(section);
                setSlideDirection('right');
                navigate(`/${section}`);
                
                setTimeout(() => {
                    setSlideDirection('none');
                    setIsAnimating(false);
                }, 120);
            }, 80);
        } else {
            // No submenu, just navigate immediately
            switch (section) {
                case 'chat':
                    navigate("/");
                    handleNewConversation();
                    break;
                case 'menus':
                    navigate("/menus");
                    break;
                case 'events':
                    navigate("/events");
                    break;
                default:
                    break;
            }
        }
    };




    // Handle back to main navigation
    const handleBackToMain = () => {
        setIsAnimating(true);
        setSlideDirection('left');
        
        setTimeout(() => {
            setCurrentLevel('main');
            setSlideDirection('right');
            
            setTimeout(() => {
                setSlideDirection('none');
                setIsAnimating(false);
            }, 120);
        }, 80);
    };

    // Get current page section from pathname
    const getCurrentPageSection = () => {
        const pathname = window.location.pathname;
        const pathSegments = pathname.split('/').filter(Boolean);
        const firstSegment = pathSegments[0];
        
        // Check if this section has submenu configuration
        return sectionsConfig[firstSegment] ? firstSegment : null;
    };

    // Handle swapping navigation levels without changing page
    const handleSwapNavigation = () => {
        if (currentLevel === 'main') {
            const currentPageSection = getCurrentPageSection();
            if (currentPageSection) {
                handleSecondaryNav(currentPageSection);
            }
        } else {
            handleBackToMain();
        }
    };

    // Navigation link component
    const NavLink = ({ 
        icon: IconComponent, 
        label, 
        onClick, 
        hasSubmenu = false,
        comingSoon = false 
    }) => (
        <div
            onClick={!comingSoon ? onClick : undefined}
            className={`group relative flex items-center py-3 rounded-lg cursor-pointer
                transition-all duration-150 ease-out
                hover:bg-[#f5f0f1]/15 hover:scale-[1.02] active:scale-[0.98] px-3
                ${comingSoon ? "opacity-50 cursor-not-allowed" : ""}
                `}
        >
            <div className="flex items-center justify-center w-5 h-5 shrink-0">
                {typeof IconComponent === 'string' ? (
                    <AdaptiveIcon src={IconComponent} className="w-4 h-4" />
                ) : (
                    <IconComponent className="w-4 h-4 text-[#f5f0f1]" />
                )}
            </div>
            <div className={`ml-3 transition-all duration-200 ease-out ${!isExpanded ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                <span className="text-sm font-medium text-[#f5f0f1] drop-shadow-sm">{label}</span>
            </div>
            {comingSoon && isExpanded && (
                <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-[#B0276E]/80 text-white backdrop-blur-sm rounded-full border border-[#B0276E]/30">
                    Soon
                </span>
            )}
            {hasSubmenu && isExpanded && (
                <div className="ml-auto">
                    <div className="w-1 h-1 bg-[#f5f0f1]/40 rounded-full"></div>
                </div>
            )}
        </div>
    );

    // Main navigation items
    const mainNavItems = [
        {
            icon: LuMessagesSquare,
            label: "Chat",
            onClick: () => handleSecondaryNav('chat'),
            hasSubmenu: false,
        },
        {
            icon: MenuIconWhite,
            label: "Menus",
            onClick: () => handleSecondaryNav('menus'),
            hasSubmenu: false,
        },
        {
            icon: EventIconWhite,
            label: "Events", 
            onClick: () => handleSecondaryNav('events'),
            hasSubmenu: false,
        },
        {
            icon: CourseIconWhite,
            label: "Courses",
            onClick: () => handleSecondaryNav('courses'),
            hasSubmenu: true,
        },
        {
            icon: NetworkingIcon,
            label: "Alumni",
            onClick: () => navigate("/alumni"),
            comingSoon: true,
        },
    ];

    // Get current navigation items
    const getCurrentNavItems = () => {
        if (currentLevel === 'main') {
            return mainNavItems;
        }
        
        const sectionConfig = sectionsConfig[currentLevel];
        return sectionConfig?.navItems || [];
    };

    return (
        <div className={`${isPhone && "hidden"} flex flex-col transition-all duration-300 ease-out ${!isExpanded ? "w-16" : "w-64"} h-full bg-transparent backdrop-blur-md border-r border-[#2a1a1e]/30 relative font-Poppins overflow-hidden`}>
            
            {/* Menu Toggle Button */}
            <div className="flex items-center justify-center py-4 pl-4 w-fit">
                <button
                    onClick={toggleNavExpanded}
                    className="p-2 hover:bg-[#f5f0f1]/15 hover:scale-105 active:scale-95 rounded-lg transition-all duration-150 ease-out"
                >
                    {isExpanded ? (
                        <LuPanelLeftClose className="w-5 h-5 text-[#f5f0f1]" />
                    ) : (
                        <LuPanelLeftOpen className="w-5 h-5 text-[#f5f0f1]" />
                    )}
                </button>
            </div>

            {/* Navigation Links Container */}
            <div className="flex-1 relative overflow-hidden flex flex-col">
                
                {/* Back Button for Secondary Navigation */}
                {currentLevel !== 'main' && (
                    <div className="px-3 pb-2">
                        <NavLink
                            icon={LuArrowLeft}
                            label="Back"
                            onClick={handleBackToMain}
                            isSecondary={true}
                        />
                    </div>
                )}

                {/* Navigation Items with Slide Animation */}
                <div className={`transition-all duration-200 ease-out transform ${
                    slideDirection === 'left' ? '-translate-x-full opacity-0 scale-95' : 
                    slideDirection === 'right' ? 'translate-x-0 opacity-100 scale-100' : 
                    'translate-x-0 opacity-100 scale-100'
                } ${isAnimating ? 'pointer-events-none' : ''}`}>
                    <div className="px-3 space-y-1">
                        {getCurrentNavItems().map((item, index) => (
                            <NavLink
                                key={`${currentLevel}-${index}`}
                                icon={item.icon}
                                label={item.label}
                                onClick={item.onClick}
                                hasSubmenu={item.hasSubmenu}
                                comingSoon={item.comingSoon}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Swap Navigation Button */}
            <div className="px-3 py-3 border-t border-[#2a1a1e]/20">
                <button
                    onClick={handleSwapNavigation}
                    className={`w-fit flex items-center py-2 rounded-lg 
                        transition-all duration-150 ease-out
                        hover:bg-[#f5f0f1]/15 hover:scale-105 active:scale-95 border border-[#2a1a1e]/20
                        ${isExpanded ? 'px-2' : 'px-2'} disabled:opacity-50 disabled:hover:scale-100`}
                    disabled={currentLevel === 'main' && !getCurrentPageSection()}
                >
                    <div className="flex items-center justify-center w-4 h-4 shrink-0">
                        <LuRefreshCw className="w-4 h-4 text-[#f5f0f1]" />
                    </div>
                </button>
            </div>
        </div>
    );
}